import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, 
  LogOut, 
  Clock, 
  Users,
  Download,
  Calendar,
  ChevronRight,
  Briefcase,
  User,
  TrendingUp,
  FileSpreadsheet,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportOrePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [riepilogoUtenti, setRiepilogoUtenti] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get('user_id') || null);
  const [userEntries, setUserEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  
  // Filter state
  const [filterDataDa, setFilterDataDa] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [filterDataA, setFilterDataA] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  }, []);

  // Fetch riepilogo utenti
  const fetchRiepilogo = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/time-entries/riepilogo`;
      const params = new URLSearchParams();
      if (filterDataDa) params.append('data_da', filterDataDa);
      if (filterDataA) params.append('data_a', filterDataA);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setRiepilogoUtenti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento riepilogo:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta');
        onLogout();
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  }, [filterDataDa, filterDataA, getAuthHeaders, onLogout]);

  // Fetch entries for selected user
  const fetchUserEntries = useCallback(async (userId) => {
    try {
      setLoadingEntries(true);
      let url = `${API}/time-entries`;
      const params = new URLSearchParams();
      if (filterDataDa) params.append('data_da', filterDataDa);
      if (filterDataA) params.append('data_a', filterDataA);
      params.append('user_id', userId);
      url += `?${params.toString()}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setUserEntries(response.data);
    } catch (error) {
      console.error('Errore nel caricamento registrazioni:', error);
      toast.error('Errore nel caricamento delle registrazioni');
    } finally {
      setLoadingEntries(false);
    }
  }, [filterDataDa, filterDataA, getAuthHeaders]);

  useEffect(() => {
    fetchRiepilogo();
  }, [fetchRiepilogo]);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserEntries(selectedUserId);
    }
  }, [selectedUserId, fetchUserEntries]);

  const handleExport = async () => {
    try {
      let url = `${API}/time-entries/export-excel`;
      const params = new URLSearchParams();
      if (filterDataDa) params.append('data_da', filterDataDa);
      if (filterDataA) params.append('data_a', filterDataA);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      let filename = 'ore_lavorate';
      if (filterDataDa && filterDataA) {
        filename += `_${filterDataDa}_${filterDataA}`;
      }
      link.download = `${filename}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export completato!');
    } catch (error) {
      console.error('Errore nell\'export:', error);
      toast.error('Errore durante l\'export');
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
  };

  const handleBackToList = () => {
    setSelectedUserId(null);
    setUserEntries([]);
  };

  // Calculate totals
  const totaleOreGenerale = riepilogoUtenti.reduce((acc, u) => acc + u.ore_totali + u.minuti_totali / 60, 0);

  const selectedUser = riepilogoUtenti.find(u => u.user_id === selectedUserId);

  const getCategoriaColor = (categoria) => {
    const colors = {
      'progetto': 'bg-blue-100 text-blue-700',
      'riunione': 'bg-purple-100 text-purple-700',
      'formazione': 'bg-green-100 text-green-700',
      'trasferta': 'bg-amber-100 text-amber-700',
      'amministrazione': 'bg-slate-100 text-slate-700',
      'altro': 'bg-gray-100 text-gray-700'
    };
    return colors[categoria] || colors['altro'];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reports')}
                className="hover:bg-slate-100"
                data-testid="back-to-reports-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Report
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-cyan-600" />
                  Report Ore Lavorate
                </h1>
                <p className="text-sm text-slate-600">
                  Riepilogo ore per utente e progetto
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-600">Ciao,</p>
                <p className="font-semibold text-slate-900">{user.nome} {user.cognome}</p>
              </div>
              <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {/* Filters & Actions */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Periodo:</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="date"
                  value={filterDataDa}
                  onChange={(e) => setFilterDataDa(e.target.value)}
                  className="w-40"
                  data-testid="filter-data-da"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="date"
                  value={filterDataA}
                  onChange={(e) => setFilterDataA(e.target.value)}
                  className="w-40"
                  data-testid="filter-data-a"
                />
              </div>
            </div>
            
            <Button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta Excel (Tutti gli utenti)
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Ore Totali</p>
                <p className="text-2xl font-bold text-slate-900">{totaleOreGenerale.toFixed(1)}h</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Utenti Attivi</p>
                <p className="text-2xl font-bold text-slate-900">{riepilogoUtenti.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Media per Utente</p>
                <p className="text-2xl font-bold text-slate-900">
                  {riepilogoUtenti.length > 0 ? (totaleOreGenerale / riepilogoUtenti.length).toFixed(1) : 0}h
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User List or User Detail */}
        {!selectedUserId ? (
          /* User List */
          <div className="bg-white rounded-xl shadow-md border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-600" />
                Ore per Utente
              </h2>
              <p className="text-sm text-slate-500">Clicca su un utente per vedere le registrazioni dettagliate</p>
            </div>
            
            {loading ? (
              <div className="text-center py-12 text-slate-600">
                Caricamento dati...
              </div>
            ) : riepilogoUtenti.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nessuna registrazione nel periodo selezionato</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {riepilogoUtenti.map((utente) => (
                  <div
                    key={utente.user_id}
                    onClick={() => handleUserSelect(utente.user_id)}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`user-row-${utente.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-lg">
                          {utente.user_nome?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{utente.user_nome}</p>
                          <div className="flex items-center gap-3 mt-1">
                            {/* Categories breakdown */}
                            {Object.entries(utente.per_categoria || {}).map(([cat, data]) => (
                              <span 
                                key={cat}
                                className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoriaColor(cat)}`}
                              >
                                {cat}: {data.ore}h {data.minuti > 0 ? `${data.minuti}m` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-cyan-600">
                            {utente.ore_totali}h {utente.minuti_totali > 0 ? `${utente.minuti_totali}m` : ''}
                          </p>
                          <p className="text-xs text-slate-500">
                            {Object.keys(utente.per_progetto || {}).length} progetti
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* User Detail */
          <div className="space-y-6">
            {/* Back Button & User Info */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBackToList}
                    data-testid="back-to-users-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Torna alla lista
                  </Button>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-lg">
                      {selectedUser?.user_nome?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-lg">{selectedUser?.user_nome}</p>
                      <p className="text-sm text-slate-500">
                        Totale: <span className="font-semibold text-cyan-600">
                          {selectedUser?.ore_totali}h {selectedUser?.minuti_totali > 0 ? `${selectedUser?.minuti_totali}m` : ''}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Projects Summary */}
            {selectedUser && Object.keys(selectedUser.per_progetto || {}).length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                  Ore per Progetto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(selectedUser.per_progetto).map(([codice, data]) => (
                    <div key={codice} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="font-semibold text-slate-900">{codice}</p>
                      {data.nome && <p className="text-sm text-slate-500 truncate">{data.nome}</p>}
                      <p className="text-lg font-bold text-cyan-600 mt-1">
                        {data.ore}h {data.minuti > 0 ? `${data.minuti}m` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entries List */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200">
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-cyan-600" />
                  Registrazioni Dettagliate
                </h3>
              </div>
              
              {loadingEntries ? (
                <div className="text-center py-12 text-slate-600">
                  Caricamento registrazioni...
                </div>
              ) : userEntries.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  Nessuna registrazione trovata
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {userEntries.map((entry) => (
                    <div key={entry.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-500">
                              {new Date(entry.data).toLocaleDateString('it-IT', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoriaColor(entry.categoria)}`}>
                              {entry.categoria}
                            </span>
                          </div>
                          <p className="font-medium text-slate-900">{entry.attivita}</p>
                          {entry.codice_main_solution && (
                            <p className="text-sm text-slate-500">
                              <span className="font-medium">{entry.codice_main_solution}</span>
                              {entry.nome_progetto && ` - ${entry.nome_progetto}`}
                            </p>
                          )}
                          {entry.ora_inizio && entry.ora_fine && (
                            <p className="text-xs text-slate-400 mt-1">
                              {entry.ora_inizio} - {entry.ora_fine}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-semibold text-cyan-600">
                            {entry.ore}h {entry.minuti > 0 ? `${entry.minuti}m` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportOrePage;
